import ko from "knockout";
import viewHtml from "./scrollableBar.html";
import metaCache from "flow/utils/metaCache";

class ScrollableBar {
    private scrollSpeed = 0.5;

    scrollNeeded = ko.observable(false).extend({
        rateLimit: 1
    });

    upButtonTitle = metaCache.translate("FlowHtml.Translations.Up");
    downButtonTitle = metaCache.translate("FlowHtml.Translations.Down");

    private resizeObserver: any;

    private upButton: ContinuousPressButton;
    private downButton: ContinuousPressButton;
    private touchStartY: number = 0;
    private touchCurrentY: number = 0;

    constructor(
        private readonly barElement: HTMLElement,
        public readonly outerViewModel: any,
        upButtonElement: HTMLElement,
        downButtonElement: HTMLElement
    ) {
        this.resizeObserver = new ResizeObserver(this.evaluateScrollNeed);
        this.resizeObserver.observe(this.barElement);

        this.barElement.addEventListener("wheel", this.onWheelEvent, { passive: true });
        this.barElement.addEventListener("touchstart", this.onTouchStart, { passive: true });
        this.barElement.addEventListener("touchmove", this.onTouchMove, { passive: true });
        this.barElement.addEventListener("touchend", this.onTouchEnd, { passive: true });

        this.upButton = new ContinuousPressButton(upButtonElement, this.scrollUp);
        this.downButton = new ContinuousPressButton(downButtonElement, this.scrollDown);

        this.evaluateScrollNeed();
    }

    dispose = () => {
        this.resizeObserver.disconnect();

        this.barElement.removeEventListener("wheel", this.onWheelEvent);
        this.barElement.removeEventListener("touchstart", this.onTouchStart);
        this.barElement.removeEventListener("touchmove", this.onTouchMove);
        this.barElement.removeEventListener("touchend", this.onTouchEnd);

        this.upButton.onDestroy();
        this.downButton.onDestroy();
    };

    private onWheelEvent = (event: WheelEvent) => {
        this.scroll(event.deltaY);
    };

    private onTouchStart = (event: TouchEvent) => {
        if (event.touches.length === 1) {
            this.touchStartY = event.touches[0].clientY;
        }
    };

    private onTouchMove = (event: TouchEvent) => {
        if (event.touches.length === 1) {
            this.touchCurrentY = event.touches[0].clientY;
            const deltaY = this.touchStartY - this.touchCurrentY;
            this.scroll(deltaY);
            this.touchStartY = this.touchCurrentY; // Update start position for the next move event
        }
    };

    private onTouchEnd = (_event: TouchEvent) => {
        this.touchStartY = 0;
        this.touchCurrentY = 0;
    };

    private evaluateScrollNeed = () => {
        this.scrollNeeded(this.barElement.scrollHeight > this.barElement.clientHeight);
        this.evalButtonStates();
    };

    private scroll = (deltaY: number) => {
        this.barElement.scrollTop += deltaY;
        this.evalButtonStates();
    };

    private evalButtonStates = () => {
        this.upButton.enable(this.barElement.scrollTop > 0);

        const scrollTopWhenScrolledToBottom = this.barElement.scrollHeight - this.barElement.clientHeight;
        this.downButton.enable(scrollTopWhenScrolledToBottom > Math.ceil(this.barElement.scrollTop));
    };

    private scrollUp = (timeElapsed: number) => {
        this.scroll(-this.scrollSpeed * timeElapsed);
    };

    private scrollDown = (timeElapsed: number) => {
        this.scroll(this.scrollSpeed * timeElapsed);
    };
}

class ContinuousPressButton {
    private isPressed = false;
    private lastTimestamp = 0;

    constructor(
        private readonly buttonElement: HTMLElement,
        private readonly callback: (timeElapsed: number) => void
    ) {
        this.buttonElement.addEventListener("mousedown", this.onMouseDown);
        this.buttonElement.addEventListener("mouseup", this.onMouseUp);
        this.buttonElement.addEventListener("mouseleave", this.onMouseLeave);

        this.buttonElement.addEventListener("touchstart", this.onMouseDown);
        this.buttonElement.addEventListener("touchend", this.onTouchEnd);
        this.buttonElement.addEventListener("touchcancel", this.onMouseLeave);
    }

    onDestroy = () => {
        this.buttonElement.removeEventListener("mousedown", this.onMouseDown);
        this.buttonElement.removeEventListener("mouseup", this.onMouseUp);
        this.buttonElement.removeEventListener("mouseleave", this.onMouseLeave);

        this.buttonElement.removeEventListener("touchstart", this.onMouseDown);
        this.buttonElement.removeEventListener("touchend", this.onTouchEnd);
        this.buttonElement.removeEventListener("touchcancel", this.onMouseLeave);
    };

    enable = (enable: boolean) => {
        if (enable) {
            this.buttonElement.removeAttribute("disabled");
        } else {
            this.buttonElement.setAttribute("disabled", "");
            this.isPressed = false;
        }
    };

    private getTimestamp = () => {
        return Date.now();
    };

    private onMouseDown = () => {
        this.isPressed = true;
        this.lastTimestamp = this.getTimestamp();

        window.requestAnimationFrame(this.tick);
    };

    private onMouseUp = () => {
        this.isPressed = false;
    };

    private onMouseLeave = () => {
        this.isPressed = false;
    };

    private onTouchEnd = (event: TouchEvent) => {
        this.onMouseLeave();
        event.preventDefault();
    };

    private tick = () => {
        const timeElapsed = this.getTimestamp() - this.lastTimestamp;
        this.callback(timeElapsed);

        if (this.isPressed) {
            this.lastTimestamp = this.getTimestamp();
            window.requestAnimationFrame(this.tick);
        }
    };
}

ko.components.register("ko-scrollable-bar", {
    viewModel: {
        // ReSharper disable once InconsistentNaming
        createViewModel(_params: any, componentInfo: any) {
            const componentElement = componentInfo.element as HTMLElement;
            const listWrappingElement = componentElement.querySelector("div");
            const upButton = componentElement.querySelector("[data-up]") as HTMLElement;
            const downButton = componentElement.querySelector("[data-down]") as HTMLElement;

            return new ScrollableBar(listWrappingElement, ko.dataFor(componentElement), upButton, downButton);
        }
    },
    template: viewHtml,
    synchronous: true
});
